import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';

export const LANGUAGES = ['de', 'fr', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];

/** Initial language: ?lang, then browser, then en. */
export function detectLanguage(): Language {
  const fromQuery = new URLSearchParams(window.location.search).get('lang');
  const fromBrowser = (navigator.language ?? '').slice(0, 2).toLowerCase();
  return [fromQuery, fromBrowser].find(isLanguage) ?? 'en';
}

/** Imprint and general privacy policy on vertikom.ch per language */
const LEGAL_LINKS: Record<Language, { imprint: string; vertikomPrivacy: string }> = {
  de: { imprint: 'https://www.vertikom.ch/impressum/', vertikomPrivacy: 'https://www.vertikom.ch/datenschutz/' },
  fr: { imprint: 'https://www.vertikom.ch/fr/mention-legales/', vertikomPrivacy: 'https://www.vertikom.ch/fr/protection-des-donnees/' },
  en: { imprint: 'https://www.vertikom.ch/en/legal-notice/', vertikomPrivacy: 'https://www.vertikom.ch/en/privacy-policy/' },
};

export function isLanguage(value: unknown): value is Language {
  return LANGUAGES.includes(value as Language);
}

/** Month and day names for the PrimeNG date picker, taken from Intl. */
function calendarTranslation(lang: Language) {
  const locale = `${lang}-CH`;
  const months = Array.from({ length: 12 }, (_, i) => new Date(2026, i, 1));
  // 2026-01-04 is a Sunday, PrimeNG expects Sunday first
  const days = Array.from({ length: 7 }, (_, i) => new Date(2026, 0, 4 + i));
  const format = (dates: Date[], options: Intl.DateTimeFormatOptions) =>
    dates.map((date) => new Intl.DateTimeFormat(locale, options).format(date));
  return {
    monthNames: format(months, { month: 'long' }),
    monthNamesShort: format(months, { month: 'short' }),
    dayNames: format(days, { weekday: 'long' }),
    dayNamesShort: format(days, { weekday: 'short' }),
    dayNamesMin: format(days, { weekday: 'short' }).map((day) => day.replace('.', '').slice(0, 2)),
    firstDayOfWeek: 1,
  };
}

@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly #translate = inject(TranslateService);
  readonly #document = inject(DOCUMENT);
  readonly #primeng = inject(PrimeNG);

  readonly current = signal<Language>(detectLanguage());
  // privacy: own page with the career page supplements, links to the vertikom.ch policy
  readonly legalLinks = computed(() => ({
    ...LEGAL_LINKS[this.current()],
    privacy: `/datenschutz?lang=${this.current()}`,
  }));

  constructor() {
    this.#document.documentElement.lang = this.current();
    this.#primeng.setTranslation(calendarTranslation(this.current()));
  }

  use(lang: Language): void {
    this.current.set(lang);
    this.#translate.use(lang);
    this.#document.documentElement.lang = lang;
    this.#primeng.setTranslation(calendarTranslation(lang));

    // Keep ?lang in the URL so shared links open in the same language
    const url = new URL(window.location.href);
    url.searchParams.set('lang', lang);
    window.history.replaceState(window.history.state, '', url.toString());
  }
}
